import { afterRenderEffect, inject, Service } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { selectExpressiveButtons } from '@app/generated/feature-flags.selectors';

/**
 * The root class under which m3e-angular's button styles apply (`global-styles/m3e-angular.scss`). Buttons carry
 * `matExpButton`/`matExpIconButton` whatever the flag says; the directives only add a class, `data-*` attributes and
 * (with `toggle` set) `aria-pressed`, so without this class they render as stock Angular Material buttons. A component
 * whose buttons were Expressive before the flag existed puts the same class on its own host to keep that look.
 */
export const EXPRESSIVE_BUTTONS_CLASS = 'syrf-expressive-buttons';

/**
 * Applies `expressiveButtons` (#4769) to the whole document at once: one class on `<html>`, so CDK overlays (menus,
 * dialogs, bottom sheets) follow the page that opened them, and switching the flag restyles every button without a
 * reload. Created by an app initializer in `main.ts`.
 */
@Service()
export class ExpressiveButtonsService {
  private readonly _document = inject(DOCUMENT);
  private readonly _enabled = inject(Store).selectSignal(selectExpressiveButtons);

  constructor() {
    // Set before the first render (the service is created by an app initializer, after config has loaded), so a
    // session with the flag on never paints stock buttons first; the effect then follows live changes.
    this._document.documentElement.classList.toggle(EXPRESSIVE_BUTTONS_CLASS, this._enabled());
    afterRenderEffect({
      write: () => {
        this._document.documentElement.classList.toggle(EXPRESSIVE_BUTTONS_CLASS, this._enabled());
      },
    });
  }
}
